import { motion } from "framer-motion";
import {
  LayoutGrid, Users, Wallet, Landmark, ShoppingCart,
  TrendingUp, ArrowUpRight,
} from "lucide-react";

const bars = [42, 68, 55, 82, 60, 94, 73];

/** Stylised, non-interactive ERP dashboard used as the hero visual. Illustrative — not live customer data. */
export function DashboardMockup() {
  return (
    <div className="relative" role="img" aria-label="Illustrative Alea ERP dashboard showing employee, payroll, purchase order and revenue metrics">
      {/* Glow */}
      <div aria-hidden="true" className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-tr from-blue-500/20 via-blue-400/10 to-transparent blur-2xl" />

      <motion.div
        initial={{ opacity: 0, y: 30, rotateX: 8 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden rounded-2xl border border-white/15 bg-white shadow-2xl ring-1 ring-black/5"
      >
        {/* Top bar */}
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-red-400" />
            <span className="h-3 w-3 rounded-full bg-amber-400" />
            <span className="h-3 w-3 rounded-full bg-green-400" />
          </div>
          <div className="ml-3 hidden flex-1 items-center rounded-md bg-white px-3 py-1 text-[11px] text-slate-400 ring-1 ring-slate-200 sm:flex">
            app.aleaerp.com/dashboard
          </div>
        </div>

        <div className="flex">
          {/* Sidebar */}
          <div className="hidden w-14 flex-col items-center gap-4 bg-[#0a2d4a] py-5 sm:flex">
            {[LayoutGrid, Users, Wallet, Landmark, ShoppingCart].map((Icon, i) => (
              <div
                key={i}
                className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                  i === 0 ? "bg-blue-500 text-white" : "text-white/50"
                }`}
              >
                <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
              </div>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 bg-[#f8fafc] p-4 sm:p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                  Overview
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[8px] font-semibold normal-case tracking-normal text-slate-400">
                    Sample data
                  </span>
                </div>
                <div className="text-sm font-bold text-[#0a2d4a]">Company Dashboard</div>
              </div>
              <div aria-hidden="true" className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-700" />
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2.5">
              {[
                { label: "Employees", value: "1,284", trend: "+4.2%" },
                { label: "Payroll", value: "₹2.4Cr", trend: "+1.8%" },
                { label: "Open POs", value: "37", trend: "+12%" },
              ].map((kpi, i) => (
                <motion.div
                  key={kpi.label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.12, duration: 0.5 }}
                  className="rounded-xl border border-slate-100 bg-white p-2 sm:p-3 shadow-sm"
                >
                  <div className="text-[9px] sm:text-[10px] font-medium text-slate-400 truncate">{kpi.label}</div>
                  <div className="mt-0.5 sm:mt-1 text-xs sm:text-base font-bold text-[#0a2d4a]">{kpi.value}</div>
                  <div className="mt-0.5 inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] font-semibold text-green-600">
                    <ArrowUpRight className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    {kpi.trend}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Chart */}
            <div className="mt-3 rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-[#0a2d4a]">Revenue & Headcount</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600">
                  <TrendingUp className="h-3 w-3" /> This quarter
                </span>
              </div>
              <div className="flex h-24 items-end gap-2">
                {bars.map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    transition={{ delay: 0.6 + i * 0.07, duration: 0.6, ease: "easeOut" }}
                    className="flex-1 rounded-t-md bg-gradient-to-t from-blue-600 to-blue-400"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating approval card */}
      <motion.div
        initial={{ opacity: 0, x: 24, y: 12 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 1, duration: 0.7 }}
        className="absolute -bottom-5 -right-3 hidden rounded-xl border border-slate-100 bg-white p-3 shadow-xl sm:block"
      >
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 text-green-600">
            <ArrowUpRight className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] font-bold text-[#0a2d4a]">PO Approved</div>
            <div className="text-[10px] text-slate-400">Routed to Finance</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
